/**
 * PermanentBadgeSection — bloc badge médiateur sur la fiche permanent.
 *
 * iter110n · CEO 2026-09-19 — un permanent avec au moins un contrat SIGNED
 * doit pouvoir gérer son badge (association + photo) et le télécharger en
 * PDF à volonté (perte, ré-impression…). Miroir du flux candidat.
 */
import { useEffect, useRef, useState } from "react";
import { Camera, Download, IdCard, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";


export default function PermanentBadgeSection({ pid, permanent, hasSignedContract, onSaved }) {
    const [associations, setAssociations] = useState([]);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [photoBust, setPhotoBust] = useState(Date.now());
    const [selectedAsso, setSelectedAsso] = useState(permanent?.association || "");
    const fileRef = useRef(null);

    useEffect(() => {
        setSelectedAsso(permanent?.association || "");
    }, [permanent?.association]);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/permanents/badges/associations`);
                setAssociations(data.associations || []);
            } catch {
                setAssociations([]);
            }
        })();
    }, []);

    const hasPhoto = Boolean(permanent?.badge_photo?.storage_path);
    // iter110n · CEO 2026-09-22 (correctif) — la photo est optionnelle,
    // le badge PDF génère un placeholder "Pas de photo" si absente. Seuls
    // contrat signé + code_rd + association bloquent réellement.
    const canGenerate =
        hasSignedContract && permanent?.code_rd && permanent?.association;

    async function saveAssociation(nextValue) {
        // iter110n · CEO 2026-09-22 — auto-save : on prend la valeur passée
        // par le onChange (nextValue) plutôt que le state (pas encore commit).
        const value = nextValue !== undefined ? nextValue : selectedAsso;
        setSaving(true);
        try {
            await api.patch(`/permanents/${pid}/association`, {
                association: value || null,
            });
            toast.success(value ? "Association enregistrée" : "Association retirée");
            onSaved?.();
        } catch (e) {
            toast.error("Sauvegarde impossible : " + (e?.response?.data?.detail || e.message));
        } finally {
            setSaving(false);
        }
    }

    async function uploadPhoto(file) {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/permanents/${pid}/badge/photo`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Photo badge enregistrée");
            setPhotoBust(Date.now());
            onSaved?.();
        } catch (e) {
            toast.error("Upload impossible : " + (e?.response?.data?.detail || e.message));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    }

    async function generateBadge() {
        setGenerating(true);
        try {
            const res = await api.get(`/permanents/${pid}/badge.pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `badge_${permanent.code_rd || pid.slice(0, 8)}_${permanent.last_name || "permanent"}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Badge généré · découpe le long des pointillés noirs (85 × 50 mm)");
        } catch (e) {
            const detail = e?.response?.data?.detail
                || (e?.response?.data instanceof Blob
                    ? "Prérequis manquants — voir bloc badge"
                    : e.message);
            toast.error("Génération impossible : " + detail);
        } finally {
            setGenerating(false);
        }
    }

    return (
        <div className="card-soft p-6" data-testid="permanent-badge-card">
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                <div>
                    <div className="flex items-center gap-2">
                        <IdCard className="h-4 w-4 text-shariti-orange" />
                        <h2 className="text-lg font-black text-shariti-black">Badge médiateur</h2>
                    </div>
                    <p className="text-[12px] text-slate-500 mt-0.5 leading-snug">
                        Format 85 × 50 mm (compatible porte-badge terrain). Recto + verso · 10 exemplaires par page A4.
                        Pointillés de découpe renforcés (CEO 2026-09-19).
                    </p>
                </div>
                <button
                    onClick={generateBadge}
                    disabled={!canGenerate || generating}
                    data-testid="permanent-badge-generate-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-shariti-orange text-white px-4 py-2 text-[11px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    title={
                        !hasSignedContract ? "Signez d'abord un contrat"
                            : !permanent?.code_rd ? "Code recruteur manquant"
                                : !permanent?.association ? "Choisir une association"
                                    : "Télécharger le badge PDF (avec ou sans photo)"
                    }
                >
                    {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                    Télécharger le badge PDF
                </button>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
                {/* Photo badge */}
                <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">
                        Photo badge
                    </div>
                    <div
                        className="relative rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 aspect-[4/5] overflow-hidden flex items-center justify-center"
                        data-testid="permanent-badge-photo-slot"
                    >
                        {hasPhoto ? (
                            <img
                                key={photoBust}
                                src={`${process.env.REACT_APP_BACKEND_URL}/api/permanents/${pid}/badge/photo?t=${photoBust}`}
                                alt={`Photo badge de ${permanent.first_name} ${permanent.last_name}`}
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="text-center px-4 text-[11px] font-semibold text-slate-400 leading-snug">
                                Aucune photo · portrait ≥ 400 × 500 px recommandé
                            </div>
                        )}
                    </div>
                    <input
                        type="file"
                        ref={fileRef}
                        accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
                        className="hidden"
                        data-testid="permanent-badge-photo-input"
                        disabled={uploading}
                        onChange={(e) => uploadPhoto(e.target.files?.[0])}
                    />
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        data-testid="permanent-badge-photo-upload-btn"
                        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-shariti-orange text-shariti-orange px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-shariti-orange-soft disabled:opacity-50 transition"
                    >
                        {uploading
                            ? <Loader2 className="h-3 w-3 animate-spin" />
                            : hasPhoto
                                ? <RefreshCw className="h-3 w-3" />
                                : <Camera className="h-3 w-3" />}
                        {hasPhoto ? "Remplacer la photo" : "Uploader une photo"}
                    </button>
                </div>

                {/* Association + code_rd */}
                <div className="md:col-span-2 space-y-3">
                    <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                            Association d&apos;affectation
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <select
                                value={selectedAsso}
                                onChange={(e) => {
                                    // iter110n · CEO 2026-09-22 · auto-save
                                    // au changement — plus besoin de cliquer
                                    // sur "Enregistrer". Le badge devient
                                    // téléchargeable dès que la sélection
                                    // est faite (association passe en vert
                                    // dans la checklist en dessous).
                                    const val = e.target.value;
                                    setSelectedAsso(val);
                                    saveAssociation(val);
                                }}
                                disabled={saving}
                                data-testid="permanent-badge-association-select"
                                className="flex-1 rounded-lg border border-shariti-border bg-white px-3 py-2 text-[12px] font-bold focus:outline-none focus:ring-2 focus:ring-shariti-orange/30 focus:border-shariti-orange disabled:opacity-60"
                            >
                                <option value="">— aucune —</option>
                                {associations.map((a) => (
                                    <option key={a.value} value={a.value}>{a.label}</option>
                                ))}
                            </select>
                            {saving && (
                                <Loader2
                                    className="h-4 w-4 animate-spin text-slate-400"
                                    data-testid="permanent-badge-association-saving"
                                />
                            )}
                        </div>
                        <div className="mt-1 text-[10px] text-slate-500">
                            Le préfixe du code affiché sur le badge (AT/FD/HI/SE) s&apos;adapte automatiquement.
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <ChecklistItem
                            ok={hasSignedContract}
                            label="Contrat signé"
                            ko="Aucun contrat signé"
                        />
                        <ChecklistItem
                            ok={Boolean(permanent?.code_rd)}
                            label={`Code ${permanent?.code_rd || "—"}`}
                            ko="Code recruteur manquant"
                        />
                        <ChecklistItem
                            ok={Boolean(permanent?.association)}
                            label="Association renseignée"
                            ko="Choisir une association"
                        />
                        {/* iter110n · CEO 2026-09-22 · La photo est
                            optionnelle — on garde la case en tant qu'INDICE
                            visuel (vert = photo présente, ambre = pas encore
                            mais pas bloquant) mais on n'utilise plus le
                            style rouge "manquant". */}
                        <PhotoStatusItem hasPhoto={hasPhoto} />
                    </div>
                </div>
            </div>
        </div>
    );
}


function ChecklistItem({ ok, label, ko }) {
    return (
        <div
            className={`rounded-2xl border p-3 flex items-center gap-2 ${
                ok ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                   : "border-slate-200 bg-slate-50 text-slate-500"
            }`}
            data-testid={`permanent-badge-checklist-${ok ? "ok" : "ko"}`}
        >
            <span className={`h-2 w-2 rounded-full shrink-0 ${ok ? "bg-emerald-500" : "bg-slate-300"}`} />
            <span className="text-[11px] font-bold">{ok ? label : ko}</span>
        </div>
    );
}


/**
 * iter110n · CEO 2026-09-22 · Item photo distinct des blockers.
 * Vert = photo présente (badge parfait), ambre = pas de photo (badge
 * imprimable avec placeholder "Pas de photo"). Jamais rouge, jamais
 * bloquant. Distingué des autres checks par un style spécifique.
 */
function PhotoStatusItem({ hasPhoto }) {
    return (
        <div
            className={`rounded-2xl border p-3 flex items-center gap-2 ${
                hasPhoto
                    ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                    : "border-amber-200 bg-amber-50 text-amber-800"
            }`}
            data-testid={`permanent-badge-photo-status-${hasPhoto ? "ok" : "missing"}`}
        >
            <span
                className={`h-2 w-2 rounded-full shrink-0 ${
                    hasPhoto ? "bg-emerald-500" : "bg-amber-500"
                }`}
            />
            <span className="text-[11px] font-bold">
                {hasPhoto ? "Photo badge uploadée" : "Photo optionnelle (badge OK sans)"}
            </span>
        </div>
    );
}
